import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthLayout from "../components/AuthLayout";
import { Input, Label } from "../components/ui/Input";
import { Button } from "../components/ui/Button";
import { Warning } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorCode(null);
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      setErrorCode(result.code || null);
      return;
    }
    const u = result.user;
    if (u.role === "admin" || u.role === "superadmin") {
      navigate(from || "/admin", { replace: true });
    } else if (u.status === "aktiv") {
      navigate(from || "/dashboard", { replace: true });
    } else {
      navigate("/pending", { replace: true });
    }
  };

  return (
    <AuthLayout
      title="Login"
      subtitle="Melden Sie sich mit Ihrer registrierten E-Mail-Adresse an."
      footer={
        <>
          Noch kein Konto?{" "}
          <Link
            to="/register"
            data-testid="login-link-register"
            className="font-medium text-ink-900 underline underline-offset-4 hover:no-underline"
          >
            REGISTRIEREN
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
        <div>
          <Label htmlFor="email">E-Mail-Adresse</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre.adresse@firma.at"
            data-testid="login-email-input"
          />
        </div>
        <div>
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="login-password-input"
          />
        </div>

        {error && (
          <div
            data-testid="login-error"
            role="alert"
            className="flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
          >
            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
            <div className="space-y-2">
              <span>{error}</span>
              {errorCode === "must_set_password" && (
                <div className="pt-1">
                  <Link
                    to="/passwort-vergessen"
                    data-testid="login-must-set-password-link"
                    className="inline-flex items-center text-xs uppercase tracking-widest font-semibold underline underline-offset-4 hover:no-underline"
                  >
                    Neuen Einladungs-Link anfordern →
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        <Button
          type="submit"
          variant="brand"
          size="lg"
          className="w-full"
          disabled={submitting}
          data-testid="login-submit-btn"
        >
          {submitting ? "Wird angemeldet…" : "LOGIN"}
        </Button>

        <div className="text-center pt-2">
          <Link
            to="/passwort-vergessen"
            data-testid="login-forgot-link"
            className="text-xs uppercase tracking-widest font-semibold text-warm-500 hover:text-ink-900 transition-crisp underline-offset-4 hover:underline"
          >
            Passwort vergessen?
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
